import { useSuspenseLiveQuery } from "@/hooks/useSuspenseLiveQuery";
import { deriveWallet } from "@/moby/repo";
import { mui } from "@/mui";
import { bytesToHex } from "@noble/curves/abstract/utils";
import { ChevronDownIcon, KeyRoundIcon, InfoIcon } from "lucide-react";
import { useState, useCallback, useEffect, type FormEvent } from "react";
import { toast } from "sonner";
import { Link, useLocation } from "wouter";
import { z } from "zod";
import { OnboardingPageLayout } from "./onboarding-page-layout";
import { useMobyRepo, useSession } from "./provider";
import { useLocationHash } from "@/hooks/useLocationHash";
import { tryPersistWithoutPromptingUser } from "@/lib/storage";

export function OnboardingLoginPage() {
  const repo = useMobyRepo();
  const session = useSession();
  const hash = useLocationHash();
  const wallets = useSuspenseLiveQuery(() => repo.getWallets(), []);
  const [, navigate] = useLocation();

  // SAFE REDIRECTS: useEffect fires AFTER paint, not during React's commit phase.
  // The old <Redirect> used useLayoutEffect which, combined with MobyChatPageLayout's
  // Redirect, created a synchronous redirect loop during SPA transitions → error #185.
  useEffect(() => {
    if (session !== undefined) {
      // setTimeout(0): escape React's effect flush to prevent useSyncExternalStore tearing.
      // wouter's navigate() calls history.replaceState synchronously → fires subscribers
      // mid-flush → error #185 if multiple effects navigate in the same flush.
      const t = setTimeout(() => navigate(`/chats${hash}`, { replace: true }), 0);
      return () => clearTimeout(t);
    } else if (wallets.length === 0) {
      const t = setTimeout(() => navigate(`/onboarding${hash}`, { replace: true }), 0);
      return () => clearTimeout(t);
    }
  }, [session, wallets.length, hash, navigate]);

  if (session !== undefined || wallets.length === 0) {
    return null;
  }

  return (
    <OnboardingPageLayout
      title={`Welcome Back`}
      description={`Please enter your password in order to login again.`}
      form={<LoginForm />}
    />
  );
}

export const loginFormSchema = z.object({
  password: z.string().min(1, "Password is required."),
});

export function LoginForm() {
  const repo = useMobyRepo();
  const wallets = useSuspenseLiveQuery(() => repo.getWallets(), []);
  const [selectedWallet, setSelectedWallet] = useState(wallets[0]!);
  const [oauthStatus, setOauthStatus] = useState<'idle' | 'pending'>('idle');
  const [oauthProvider, setOauthProvider] = useState<'google' | 'outlook' | 'yahoo'>('google');
  const [showRecovery, setShowRecovery] = useState(false);

  // Generic OAuth flow — works for any provider (matches onboarding page pattern)
  const startOAuth = useCallback(async (provider: 'google' | 'outlook' | 'yahoo') => {
    let gatewayUrl = localStorage.getItem('moby_pref_emailGatewayUrl')?.trim() || '';
    if (!gatewayUrl) {
      gatewayUrl = window.location.origin;
      localStorage.setItem('moby_pref_emailGatewayUrl', gatewayUrl);
    }

    const providerLabel = provider === 'google' ? 'Google' : provider === 'outlook' ? 'Outlook' : 'Yahoo';
    setOauthStatus('pending');
    setOauthProvider(provider);

    try {
      const configRes = await fetch(`${gatewayUrl}/api/email/oauth/${provider}/config`);
      const configData = await configRes.json();
      if (!configData.configured) {
        toast.error(`${providerLabel} Sign-In is not configured on this server yet.`);
        setOauthStatus('idle');
        return;
      }

      const authRes = await fetch(`${gatewayUrl}/api/email/oauth/${provider}/auth-url`);
      const authData = await authRes.json();
      if (!authData.success || !authData.authUrl) {
        toast.error(authData.error || `Failed to start ${providerLabel} Sign-In`);
        setOauthStatus('idle');
        return;
      }

      // Save state + provider for verification on return + flag for login mode
      localStorage.setItem('moby-oauth-state', authData.state);
      localStorage.setItem('moby-oauth-provider', provider);
      localStorage.setItem('moby-oauth-login-mode', 'true');

      // Redirect to provider (same tab) — callback redirects to /onboarding
      window.location.href = authData.authUrl;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `${providerLabel} Sign-In failed`);
      setOauthStatus('idle');
    }
  }, []);

  const startGoogleOAuth = useCallback(() => startOAuth('google'), [startOAuth]);
  const startOutlookOAuth = useCallback(() => startOAuth('outlook'), [startOAuth]);
  const startYahooOAuth = useCallback(() => startOAuth('yahoo'), [startOAuth]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const data = new FormData(e.target as HTMLFormElement);
    const form = loginFormSchema.safeParse({
      password: data.get("password"),
    });

    if (!form.success) {
      for (const error of form.error.errors) {
        toast.error(error.message);
      }
      return;
    }

    // Storage check — softened to warnings. Persistent storage is optional;
    // the silent retry in MobyProvider will keep trying on future visits.
    const storage = await tryPersistWithoutPromptingUser();
    if (storage === "never") {
      toast.warning(
        "This browser may not fully support persistent storage. Your data could be lost if the browser clears storage. Consider using Google Chrome for the best experience.",
        { duration: 8000 },
      );
    } else if (storage === "prompt") {
      toast.warning(
        "Persistent storage not yet granted. MobyNet will automatically retry on future visits. You can also grant it in your browser's site settings.",
        { duration: 8000 },
      );
    }

    const derived = await deriveWallet(
      form.data.password,
      selectedWallet.proof,
    );

    const derivedPublicKey = bytesToHex(derived.publicKey);
    if (derivedPublicKey !== selectedWallet.address) {
      toast.error("Invalid password.");
      return;
    }

    toast.success(`Welcome back, ${selectedWallet.name}.`);

    await repo.setSession({
      name: selectedWallet.name,
      address: derivedPublicKey,
      secret: bytesToHex(derived.secretKey),
    });
  };

  return (
    <form
      className="rounded-medium bg-surface border-outline-variant text-on-surface mt-10 border p-4"
      onSubmit={onSubmit}
    >
      <input hidden type="text" autoComplete="username" />

      <div className="-mx-4">
        <div>
          <mui.Select.Root
            value={selectedWallet.address}
            onValueChange={(walletAddress) => {
              const wallet = wallets.find((w) => w.address === walletAddress);
              if (wallet === undefined) {
                toast.error(
                  `The wallet information for ${walletAddress} is not available. Please try again.`,
                );
                return;
              }

              setSelectedWallet(wallet);
            }}
          >
            <mui.Select.Trigger
              className="flex gap-4 pt-6 pb-2"
              variant="surface"
            >
              <mui.Select.Value
                className="relative min-w-0 flex-1"
                render={<div />}
              >
                {() => (
                  <>
                    <label className="text-body-sm absolute -top-4 left-0 size-full">
                      Account
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-body-lg shrink-0">
                        {selectedWallet.name}
                      </span>
                      <code className="bg-surface-container text-on-surface truncate px-1 py-0.5 text-xs">
                        {selectedWallet.address}
                      </code>
                    </div>
                  </>
                )}
              </mui.Select.Value>
              <mui.Select.Icon className="-mt-2">
                <ChevronDownIcon size={24} />
              </mui.Select.Icon>
            </mui.Select.Trigger>
            <mui.Select.Portal>
              <mui.Select.Positioner sideOffset={8}>
                <mui.Select.Popup>
                  {wallets.map((wallet) => (
                    <mui.Select.Item
                      key={wallet.address}
                      value={wallet.address}
                    >
                      <mui.Select.ItemText>{wallet.name}</mui.Select.ItemText>
                    </mui.Select.Item>
                  ))}
                </mui.Select.Popup>
              </mui.Select.Positioner>
            </mui.Select.Portal>
          </mui.Select.Root>
        </div>

        <div className="relative">
          <mui.Input
            type="password"
            id="password"
            name="password"
            autoComplete="current-password"
          />
          <mui.FloatingLabel htmlFor="password">Password</mui.FloatingLabel>
        </div>
      </div>

      <div className="mt-4 flex justify-center gap-2">
        <Link to="/onboarding" asChild>
          <mui.Link
            variant="secondary-container"
            className="rounded-small w-full"
            filled
          >
            Register
          </mui.Link>
        </Link>
        <mui.Button
          type="submit"
          className="rounded-small w-full"
          filled
          variant="primary"
        >
          Login
        </mui.Button>
      </div>

      {/* Divider */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 border-t border-outline-variant" />
        <span className="text-label-sm text-on-surface-variant">or</span>
        <div className="flex-1 border-t border-outline-variant" />
      </div>

      {/* OAuth sign-in buttons — for returning users who signed up via OAuth */}
      <div className="mt-3 flex flex-col gap-2">
        {/* Sign in with Google */}
        <button
          type="button"
          className={`w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-outline-variant bg-surface transition-colors ${
            oauthStatus !== 'pending' ? 'hover:bg-surface-variant' : 'opacity-50 cursor-not-allowed'
          }`}
          onClick={startGoogleOAuth}
          disabled={oauthStatus === 'pending'}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span className="text-body-md text-on-surface font-medium">
            {oauthStatus === 'pending' && oauthProvider === 'google' ? 'Signing in...' : 'Sign in with Google'}
          </span>
        </button>

        {/* Sign in with Outlook */}
        <button
          type="button"
          className={`w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-outline-variant bg-surface transition-colors ${
            oauthStatus !== 'pending' ? 'hover:bg-surface-variant' : 'opacity-50 cursor-not-allowed'
          }`}
          onClick={startOutlookOAuth}
          disabled={oauthStatus === 'pending'}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 0 1-.588.234h-8.522v-8.07l1.5 1.11 1.848-1.31V8.39l-1.848 1.31-1.5-1.14v-.173H24z" fill="#0364B8"/>
            <path d="M16.5 10.405l-1.848 1.31-1.5-1.11v8.07h-4.5V7.387h7.848v2.018z" fill="#0078D4"/>
            <path d="M8.652 4.652v15.348A.782.782 0 0 1 7.87 20.8L0 18.652V5.348L7.87 3.2a.782.782 0 0 1 .782.782v.67z" fill="#0078D4"/>
            <path d="M5.87 9.174c-.392-.234-.87-.352-1.435-.352-.609 0-1.1.2-1.478.6-.378.4-.566.918-.566 1.556 0 .66.183 1.192.548 1.596.366.404.848.606 1.448.606.574 0 1.06-.122 1.457-.366v1.174c-.43.2-.944.3-1.54.3-.896 0-1.613-.287-2.152-.86C1.384 12.854 1.152 12.109 1.152 11c0-1.122.283-2.009.848-2.661.566-.652 1.322-.978 2.27-.978.548 0 1.04.1 1.474.296v1.517h.126z" fill="#fff"/>
          </svg>
          <span className="text-body-md text-on-surface font-medium">
            {oauthStatus === 'pending' && oauthProvider === 'outlook' ? 'Signing in...' : 'Sign in with Outlook'}
          </span>
        </button>

        {/* Sign in with Yahoo */}
        <button
          type="button"
          className={`w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-outline-variant bg-surface transition-colors ${
            oauthStatus !== 'pending' ? 'hover:bg-surface-variant' : 'opacity-50 cursor-not-allowed'
          }`}
          onClick={startYahooOAuth}
          disabled={oauthStatus === 'pending'}
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M13.371 4l-1.737 4.028L9.898 4H5l4.474 8.884L7.654 20h4.898l1.812-7.116L18.838 20h4.898l-1.82-7.116L24 4h-4.898l-1.737 4.028L15.628 4h-2.257z" fill="#720E9E"/>
            <path d="M0 7.5h4.5v12H0z" fill="#720E9E"/>
            <circle cx="2.25" cy="4.25" r="2.25" fill="#720E9E"/>
          </svg>
          <span className="text-body-md text-on-surface font-medium">
            {oauthStatus === 'pending' && oauthProvider === 'yahoo' ? 'Signing in...' : 'Sign in with Yahoo'}
          </span>
        </button>
      </div>

      {/* Password Recovery */}
      <div className="mt-4 border-t border-outline-variant pt-3">
        <button
          type="button"
          onClick={() => setShowRecovery(!showRecovery)}
          className="flex w-full items-center justify-between text-left"
        >
          <span className="text-label-sm text-on-surface-variant flex items-center gap-1.5">
            <KeyRoundIcon size={14} />
            Forgot your password?
          </span>
          <ChevronDownIcon
            size={16}
            className={`text-on-surface-variant transition-transform duration-200 ${showRecovery ? 'rotate-180' : ''}`}
          />
        </button>

        {showRecovery && (
          <div className="mt-2 flex flex-col gap-3 animate-in fade-in slide-in-from-top-1 duration-200">
            <div className="rounded-lg border border-outline-variant bg-surface-container-lowest p-3">
              <div className="flex items-start gap-2">
                <InfoIcon size={14} className="text-on-surface-variant mt-0.5 shrink-0" />
                <div className="text-body-sm text-on-surface-variant leading-relaxed">
                  <p className="font-medium text-on-surface">Signed up with Google, Outlook, or Yahoo?</p>
                  <p className="mt-1">
                    Use the sign-in buttons above to log in automatically.
                    Your account password was linked to your email provider during registration.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-outline-variant bg-surface-container-lowest p-3">
              <div className="flex items-start gap-2">
                <InfoIcon size={14} className="text-on-surface-variant mt-0.5 shrink-0" />
                <div className="text-body-sm text-on-surface-variant leading-relaxed">
                  <p className="font-medium text-on-surface">Created your account manually?</p>
                  <p className="mt-1">
                    MobyNet uses end-to-end encryption with no central server.
                    Your password never leaves your device, so it cannot be reset remotely.
                    Please try any passwords you may have used during registration.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-error/30 bg-error/5 p-3">
              <p className="text-body-sm text-on-surface-variant">
                <span className="font-medium text-error">Last resort:</span>{' '}
                You can create a new account, but your previous conversations and contacts will not carry over.
              </p>
              <Link to="/onboarding?new=1" asChild>
                <mui.Link
                  variant="tertiary-container"
                  className="rounded-small mt-2 w-full text-center border border-error/30"
                  filled
                >
                  Create New Account
                </mui.Link>
              </Link>
            </div>
          </div>
        )}
      </div>
    </form>
  );
}
